import React, { useEffect, useState } from "react";
import GenericTable from "../GenericTable/GenericTable";
import { CircularProgress, Alert, Snackbar, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import config from "../../../config";

const syncLogsColumns = [
  {
    id: "store_name",
    label: "Store Name",
    render: (row) => row.store_name || "-"
  },
  {
    id: "type",
    label: "Sync Type",
    render: (row) => {
      const typeLabels = {
        product: "Product",
        order: "Order",
        revenue: "Revenue",
        both: "Both"
      };
      return typeLabels[row.type] || row.type;
    }
  },
  {
    id: "status",
    label: "Status",
    render: (row) => {
      const statusConfig = {
        success: { label: "Success", color: "success", icon: <CheckCircleIcon /> },
        failed: { label: "Failed", color: "error", icon: <ErrorIcon /> },
        partial: { label: "Partial", color: "warning", icon: <WarningIcon /> },
      };
      const config = statusConfig[row.status] || { label: row.status, color: "default" };
      return (
        <Chip
          icon={config.icon}
          label={config.label}
          color={config.color}
          variant="outlined"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      );
    }
  },
  {
    id: "start_time",
    label: "Start Time",
    render: (row) => {
      if (!row.start_time) return "-";
      const date = new Date(row.start_time);
      return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
    }
  },
  {
    id: "end_time",
    label: "End Time",
    render: (row) => {
      if (!row.end_time) return "-";
      const date = new Date(row.end_time);
      return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
    }
  },
  {
    id: "duration",
    label: "Duration",
    render: (row) => {
      if (!row.start_time || !row.end_time) return "-";
      const start = new Date(row.start_time);
      const end = new Date(row.end_time);
      const seconds = Math.round((end - start) / 1000);
      return `${seconds}s`;
    }
  }
];

function SyncLogsTable() {
  const [loading, setLoading] = useState(true);
  const [syncLogsData, setSyncLogsData] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Get client_id from config (same as Orders page)
  const clientId = config.clientId || "0486bc6d-6d15-4ea3-8504-adefe1512324";

  useEffect(() => {
    fetchSyncLogs();
  }, []);

  const fetchSyncLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("🔍 Fetching sync logs for client:", clientId);

      const response = await fetch(
        `${config.API_BASE_URL}/client/sync-logs/list?client_id=${clientId}`,
        {
          method: "GET",
          headers: config.getHeaders()
        }
      );

      const result = await response.json();
      console.log("📊 Sync Logs API response:", result);

      if (result.success && result.data) {
        setSyncLogsData(result.data);
        console.log(`✅ Loaded ${result.data.length} sync log records`);
      } else {
        setError(result.message || "Failed to fetch sync logs");
        console.error("❌ Sync logs fetch failed:", result.message);
      }
    } catch (err) {
      setError(err.message || "Error fetching sync logs");
      console.error("❌ Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <GenericTable
        title="Sync Logs"
        columns={syncLogsColumns}
        rows={syncLogsData}
        showView={false}
        showEdit={false}
        showDelete={false}
        dropdownFilters={["type", "status"]}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </>
  );
}

export default SyncLogsTable;

